import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useVariables} from '../../assets/objects/Variables';
import {DEFAULT_LANG} from '../../globalVariables/locals';
import * as css from './WhatsAppTemplateEditor.css';
import WhatsAppEditorWarningBadges from './components/whatsAppEditorWarningBadges/WhatsAppEditorWarningBadges';
import VariableDropdown from '../variableDropdown/VariableDropdown';
import {WHATSAPP_MAX_TEXT_LENGTH} from '../../pages/branding/whatsAppScreen/whatsAppValidation';
import WhatsAppEditorFooter from './components/whatsAppEditorFooter/WhatsAppEditorFooter';
import {DirectionProvider} from './context/DirectionContext';
import {TemplateItem, TemplateStructure, TextTemplateItem} from './types/templateStructure';
import {useTemplateStructure} from './hooks/useTemplateStructure';
import {useVariableInsertion} from './hooks/useVariableInsertion';
import {useFormatting} from './hooks/useFormatting';
import {renderTemplateItem} from './utils/renderTemplateItem';
import {calculateTotalCharacters, getFilledItemsCount, getTotalEditableItemsCount} from './utils/templateStatistics';
import {hasDisabledVariables} from './utils/variableUtils';

interface WhatsAppTemplateEditorProps {
    events?: {onChange?: (content: string) => void, onSave?: (valuesArray: TextTemplateItem[]) => void};
    templateStructure: TemplateStructure;
    dependencies?: any[];
    'data-cy'?: string;
}

function WhatsAppTemplateEditor({
    events = {onChange: undefined, onSave: undefined},
    templateStructure,
    dependencies = [],
    'data-cy': dataCy
}: WhatsAppTemplateEditorProps) {
    const {defaultVariables, disabledVariables} = useVariables();
    const {t} = useTranslation('variables');
    const currentLang = useSelector((s: any) => s.branding?.currentLang || DEFAULT_LANG);
    const eventType = useSelector((s: any) => s.event?.type || '');
    const direction = currentLang !== 'he' ? 'ltr' : 'rtl';
    const variables = useMemo(() => ({...defaultVariables, ...disabledVariables}), [defaultVariables, disabledVariables]);
    
    // Template structure state management
    const {
        structure,
        hasChanged,
        handleItemChange,
        prepareForSave,
        clearEditing
    } = useTemplateStructure({initialStructure: templateStructure, dependencies, onChange: events.onChange});
    
    const disabledVariablesWarning = useMemo(() => hasDisabledVariables(structure, disabledVariables), [structure, disabledVariables]);
    const totalCharacters = useMemo(() => calculateTotalCharacters(structure), [structure]);
    const filledItemsCount = useMemo(() => getFilledItemsCount(structure), [structure]);
    const totalItemsCount = useMemo(() => getTotalEditableItemsCount(structure), [structure]);
    const isCharCountExceeded = totalCharacters > WHATSAPP_MAX_TEXT_LENGTH;
    
    // Variable insertion hook
    const {handleInsertVariable, setLastFocusedEditable, updateSavedCursorOffset} = useVariableInsertion({
        structure,
        defaultVariables,
        onItemChange: handleItemChange
    });
    
    // Formatting hook
    const {handleFormat} = useFormatting({structure, onItemChange: handleItemChange});
    
    const handleSave = useCallback(() => {
        if(!events.onSave) return;
        if(isCharCountExceeded) {
            return;
        }
        const valuesArray = prepareForSave();
        events.onSave(valuesArray);
        clearEditing();
    }, [events.onSave, isCharCountExceeded, prepareForSave, clearEditing]);
    
    return (
        <DirectionProvider direction={direction}>
            <css.wrapper data-cy={dataCy}>
                <WhatsAppEditorWarningBadges
                    totalCharacters={totalCharacters}
                    filledParagraphsCount={filledItemsCount}
                    totalParagraphsCount={totalItemsCount}
                    hasDisabledVariables={disabledVariablesWarning}
                />

                <VariableDropdown
                    onInsertVariable={handleInsertVariable}
                    defaultVariables={defaultVariables}
                    disabledVariables={disabledVariables}
                    eventType={eventType}
                    remainingChars={WHATSAPP_MAX_TEXT_LENGTH - totalCharacters}
                />
                
                {structure.map((item: TemplateItem, index: number) => renderTemplateItem({
                    item,
                    index,
                    handleItemChange,
                    setLastFocusedEditable,
                    updateSavedCursorOffset,
                    variables,
                    t,
                }))}

                <WhatsAppEditorFooter
                    hasChanged={hasChanged}
                    saveDisabled={isCharCountExceeded}
                    totalCharacters={totalCharacters}
                    isCharCountExceeded={isCharCountExceeded}
                    onSave={events.onSave ? handleSave : undefined}
                    onFormat={handleFormat}
                />
            </css.wrapper>
        </DirectionProvider>
    );
}

export default WhatsAppTemplateEditor;
